import { useEffect, useState } from 'react';
import { useUserContext } from '../../../../providers/UserProvider';
import { useWorkpaperContext } from '../../../../providers/WorkpaperProvider';
import { useCreateInternalComment } from '../../../../queries/use-create-internal-comment';
import { Avatar, ButtonV2 } from '@cimplico/components';
import Editor from '../../../../components/rich-text-editor/Editor';
import { useIntercom } from 'react-use-intercom';
import { intercomEvents } from '../../../../intercom-events';

type NewCommentProps = {
    worksheetId: string;
    onAction?: () => void;
    expanded?: boolean;
    onExpand?: () => void;
};
function NewComment({ worksheetId, onAction, expanded: _expanded = false, onExpand }: NewCommentProps): JSX.Element {
    const user = useUserContext();
    const { trackEvent } = useIntercom();
    const { id: workpaperId, readOnly } = useWorkpaperContext();
    const [expanded, setExpanded] = useState(_expanded);
    const [comment, setComment] = useState({ rawComment: '', structuredComment: '' });

    const createInternalCommentQuery = useCreateInternalComment(workpaperId);

    useEffect(() => {
        setExpanded(_expanded);
    }, [_expanded]);

    function save(): void {
        if (!comment.rawComment) {
            return;
        }

        const body = {
            worksheetId,
            rawComment: comment.rawComment,
            structuredComment: JSON.parse(comment.structuredComment),
        };

        createInternalCommentQuery.mutate(
            {
                internalComments: [body],
            },
            {
                onSuccess: () => {
                    onAction && onAction();
                    setComment({ rawComment: '', structuredComment: '' });
                    trackEvent(intercomEvents.internalComment.created.event);
                },
            }
        );
    }

    if (!expanded) {
        return (
            <div className="flex items-center gap-2">
                <Avatar
                    color={user.profileHexColour}
                    alt={`${user.firstName[0]}${user.lastName[0]}`.toUpperCase()}
                    src={user.avatarUrl}
                    size="xs"
                />
                <ButtonV2
                    type="hollow"
                    label="Add a new comment..."
                    size="sm"
                    fill
                    isDisabled={readOnly}
                    onClick={(): void => {
                        setExpanded(true);
                        onExpand && onExpand();
                    }}
                />
            </div>
        );
    }

    return (
        <div>
            {/* AUTHOR */}
            <div className="flex items-center gap-2">
                <Avatar
                    color={user.profileHexColour}
                    alt={`${user.firstName[0]}${user.lastName[0]}`.toUpperCase()}
                    src={user.avatarUrl}
                    size="xs"
                />
                <p className="text-sm text-gray-800 font-medium py-1 px-2 h-[30px]">
                    {user.firstName} {user.lastName}
                </p>
            </div>

            {/* EDITOR */}
            <div className="mt-2">
                <div className="h-[250px]">
                    <Editor
                        onContentChange={(raw, structured): void => {
                            setComment({ rawComment: raw, structuredComment: structured });
                        }}
                    />
                </div>
                {/* SAVE + CANCEL */}
                <div>
                    <div className="flex items-center gap-2 justify-end mt-1">
                        <ButtonV2
                            label="Cancel"
                            size="xs"
                            type="hollow"
                            onClick={(): void => {
                                setComment({ structuredComment: '', rawComment: '' });
                                setExpanded(false);
                                onAction && onAction();
                            }}
                            isDisabled={createInternalCommentQuery.isLoading}
                        />
                        <ButtonV2
                            label="Save"
                            size="xs"
                            type="primary"
                            onClick={save}
                            isDisabled={createInternalCommentQuery.isLoading || !comment.rawComment}
                            isLoading={createInternalCommentQuery.isLoading}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}

export default NewComment;
